import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@matix/auth';
import {
  getApiErrorCode,
  getApiErrorDetails,
  getApiErrorMessage,
  knetTerminalAPI,
  paymentAPI,
  type StockShortageDto,
} from '@matix/api-client';
import { useStockLevels } from '../../store/stockLevels';
import type {
  CreateOrderRequest,
  CreateOrderResponse,
  PaymentType,
  ReceiptData,
} from '@matix/types';
import { roundMoney } from '@matix/utils';
import { useUI } from '../../store/ui';
import { useCart, useCartTotals } from '../../store/cart';
import { orderTypeCodeFor } from '../../lib/orderTypeCode';
import { amountStep, amountText, moneyEpsilon, moneyPrecision } from '../../store/currency';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { fmt } from '../../lib/mock-data';
import { buildCreateOrderRequest, usePlaceOrder } from '../orders/usePlaceOrder';
import { PAYMENT_TYPES_QUERY_KEY } from './usePaymentTypes';
import { printAfterCharge } from '../print';
import { useLaundryPrint } from '../laundry';
import { isNetworkError, useNetwork } from '../../lib/netStatus';
import { nextProvisionalNo } from '../../store/outbox';
import { offlineBlockReason, queueSaleOffline } from '../offline/offlineSale';
import { useTenantConfig } from '../../store/tenantConfig';
import { CheckIcon, TrashIcon } from '../../components/icons';
import { GatewayPaymentDialog } from './GatewayPaymentDialog';
import { isPaymentGatewayCode, paymentGatewayForCode, type PaymentGatewayCode } from '@matix/types';
import { LoyaltyRedeemPanel } from './LoyaltyRedeemPanel';
import { useLoyalty } from '../../store/loyalty';
import { GiftCardPayPanel, type AppliedGiftCard } from './GiftCardPayPanel';
import { GiftCardRefundPanel, type GiftCardRefundChoice } from './GiftCardRefundPanel';
import { useGiftCards } from '../../store/giftCards';
import { buildDeliveryPayload, deliveryFeeOf } from '../delivery/deliveryPayload';
import { useCustomerDetail } from '../delivery/hooks';
import { cashierIdentity } from '../../lib/cashierIdentity';
import type { KnetTerminalTxn } from '@matix/types';
import { KnetTerminalDialog } from '../knet-terminal/KnetTerminalDialog';
import {
  cardSummary,
  isTerminalTender,
  unusedApprovals,
  useKnetTerminalConfig,
} from '../knet-terminal/knetTerminalFlow';

/**
 * Hosted payment gateways — Tap (-3), UPayments (-4), Tabby (-5), Tamara (-6),
 * MyFatoorah (-7) — each own a reserved Inv_PaymentType.Code (see
 * `PAYMENT_GATEWAYS` in @matix/types). The cashier flow detects one of those
 * codes and switches to the GatewayPaymentDialog instead of adding a regular
 * split — the gateway is charged BEFORE the GL voucher posts, so the normal
 * createCashOrder flow doesn't apply directly. The server lists a gateway's
 * code only while its settings screen has it enabled.
 */

/**
 * Multi-payment modal.
 *
 * Flow:
 *   1. Cashier opens via "Charge". Modal mounts with the order net
 *      pre-filled in the amount input (one-tap exact-change shortcut
 *      for the common case).
 *   2. Cashier picks a payment method by clicking it. The amount in
 *      the input is registered as a split, the input refills with the
 *      new remaining balance, and the method is dimmed if it's already
 *      in the splits list (one row per method per receipt).
 *   3. Splits accumulate in the right-side panel with an X to remove.
 *      Removing a split adds its amount back to the input automatically.
 *   4. When Σ splits ≥ order net, the "Confirm" CTA enables. Cashier
 *      can over-tender on cash methods (change due is shown); non-cash
 *      methods are clamped to remaining balance at click time.
 *   5. Confirm POSTs the splits to /api/v1/orders. Success → cart reset
 *      + toast + close. Errors stay in the modal so the cashier can
 *      retry without losing the splits.
 *
 * Idempotency: a fresh UUID per modal open. Retries within one confirm
 * click reuse it; backing out + reopening generates a new one (correct,
 * because the cart could have changed).
 */
export function PaymentModal() {
  const isOpen = useUI((s) => s.openModal === 'payment');
  // LAUNDRY pay-now (set by "Take in · pay now"): a banner here, the laundry
  // ticket instead of the plain receipt, and cleared whenever the window
  // closes — a cancelled pay-now must not turn the next sale into one.
  const laundryPayNow = useCart((s) => s.laundryPayNow);
  const membershipDates = useCart((s) => s.membershipDates);
  const printLaundryTicket = useLaundryPrint();
  useEffect(() => {
    if (!isOpen && useCart.getState().laundryPayNow) useCart.getState().setLaundryPayNow(null);
    if (!isOpen && useCart.getState().membershipDates) useCart.getState().setMembershipDates(null);
  }, [isOpen]);
  const closeModal = useUI((s) => s.closeModal);
  const showModal = useUI((s) => s.showModal);
  const lines = useCart((s) => s.lines);
  const customer = useCart((s) => s.customer);
  // Which of their addresses this order is for — only set when the customer
  // has more than one and the cashier picked in the customer panel.
  const customerAddressId = useCart((s) => s.customerAddressId);
  const table = useCart((s) => s.table);
  const delivery = useCart((s) => s.delivery);
  const deliveryDraft = useCart((s) => s.deliveryDraft);
  const discount = useCart((s) => s.discount);
  /**
   * The one manager approval the order can carry.
   *
   * A request has a single `manager_override_user_id`, but an over-limit
   * discount can now be granted in two places — the header modal or a line's
   * own. The server measures the cashier's limit against the COMBINED figure,
   * so an approval obtained for either half authorises the whole; the header's
   * is preferred only because it is the one a cashier reaches for first.
   */
  const overrideUserId =
    discount?.managerOverrideUserId ??
    lines.find((l) => l.lineDiscount?.managerOverrideUserId != null)?.lineDiscount
      ?.managerOverrideUserId ??
    null;
  const orderNote = useCart((s) => s.note);
  const setOrderNote = useCart((s) => s.setNote);
  const referenceNo = useCart((s) => s.referenceNo);
  const setReferenceNo = useCart((s) => s.setReferenceNo);
  const orderType = useCart((s) => s.orderType);
  const orderMode = useCart((s) => s.orderMode);
  const exchangeParentId = useCart((s) => s.exchangeParentId);
  const reset = useCart((s) => s.reset);
  const { cashier } = useAuth();
  const { t: tr, lang } = useLang();

  // Receipt-printing dependencies. queryClient is used to read the cached
  // PrintConfig (warmed at login by usePrintConfig in App.tsx); lastReqRef
  // captures the CreateOrderRequest synchronously before each mutate() so
  // the onSuccess callback can pass the request to printAfterCharge.
  const queryClient = useQueryClient();
  const lastReqRef = useRef<CreateOrderRequest | null>(null);

  // ── Delivery orders reach this modal too ──────────────────────────────
  //
  // The DeliveryConfirmModal collects customer / address / driver / promised
  // time and then offers "Take payment", which opens THIS modal so a delivery
  // can be settled at the counter with the same splits, change due, gift
  // cards, loyalty and gateways as any other sale. Nothing is passed between
  // the two: the form lives in the cart (`deliveryDraft`), so we rebuild the
  // sub-object the server requires for `order_type === 2` from the same rows
  // that modal wrote.
  //
  // The fee comes from the draft as well. `delivery.fee` (the legacy
  // DeliveryDetails shape) is not set in delivery mode, and useCartTotals
  // already prices the order off the draft — passing the wrong one would send
  // an `expected_total` short by exactly the fee and earn a TOTALS_MISMATCH.
  const isDeliveryOrder = orderMode === 'delivery';
  const deliveryPayload = useMemo(
    () => (isDeliveryOrder ? buildDeliveryPayload(deliveryDraft) : null),
    [isDeliveryOrder, deliveryDraft],
  );
  // The receipt prints the address the cashier picked, and finding it means
  // having the customer's ADDRESS LIST — which the cart's customer chip does
  // not carry (it is whatever row the search returned). The delivery modal has
  // already fetched the full record under this key, so this is a cache read,
  // not a second round-trip.
  const deliveryCustomer = useCustomerDetail(isDeliveryOrder ? deliveryDraft.customerId : null);
  const printCustomer = deliveryCustomer.data ?? customer;
  const deliveryArgs = useMemo(
    () => ({
      deliveryFee: isDeliveryOrder ? deliveryFeeOf(deliveryDraft) : (delivery?.fee ?? 0),
      ...(deliveryPayload ? { delivery: deliveryPayload } : {}),
    }),
    [isDeliveryOrder, deliveryDraft, delivery?.fee, deliveryPayload],
  );

  // A delivery with no customer cannot be posted (the server refuses it with
  // DELIVERY_PAYLOAD_REQUIRED), and the cashier fixes that on the delivery
  // form, not here. Send them back rather than let them register splits
  // against an order that will be rejected on Confirm.
  // `lines.length` is in there because a successful charge clears the cart AND
  // the draft before the modal closes: without it, the moment between the two
  // reads as "delivery with no customer" and bounces a finished sale back to
  // the delivery form.
  useEffect(() => {
    if (!isOpen || !isDeliveryOrder || deliveryPayload || lines.length === 0) return;
    showModal('deliveryConfirm');
  }, [isOpen, isDeliveryOrder, deliveryPayload, lines.length, showModal]);

  const t = useCartTotals();
  // "Effectively zero" threshold for every money comparison in this modal.
  // One smallest unit of the tenant's currency, capped at a cent so the
  // 2-decimal tenants keep their historical 0.01 slack: 0.01 on EGP/SAR/AED,
  // 0.001 on KWD — a hardcoded cent would treat a 0.008 KWD order as nothing
  // to collect and let a split land 10 fils short of the total.
  const EPS = moneyEpsilon();
  // Sale/return/exchange split. The pick-a-method splits UI targets the SALE
  // leg (what the cashier collects); the RETURN leg (refund) is a separate
  // method pick below. `netSettled` is what physically changes hands
  // (+collect / −refund) and drives the header + confirm label.
  const hasReturns = t.hasReturns;
  const collectTarget = roundM(t.saleTotal);
  const returnTotal = roundM(t.returnTotal);
  // Net physically settled. MUST be derived from the two ALREADY-ROUNDED legs,
  // not from the raw `t.net` — the sale leg (collectTarget) and the return
  // credit (returnTotal) are each booked at the currency's precision, so the
  // net cash must equal their rounded difference. Rounding the raw net
  // independently (`roundM(t.saleTotal - t.returnTotal)`) can land one unit off
  // the rounded legs (e.g. sale 0.754→0.75, return 0.585→0.59 gives raw net
  // 0.169→0.17, but 0.75−0.59 = 0.16). That unit then makes Σ sale payments
  // (physical + returnCredit) miss the server's sale-net check
  // (PAYMENT_SUM_MISMATCH).
  const netSettled = roundM(collectTarget - returnTotal);
  // Exchange where the customer OWES the difference (bought MORE than returned).
  // The returned goods are credited toward the new sale, so the cashier only
  // collects the NET difference — not the full sold amount. The sale leg is
  // still booked in full on the wire (the server requires Σ sale payments ==
  // sale total); handleConfirm grosses the payments back up by crediting the
  // return value onto the refund method.
  const isPositiveExchange = hasReturns && netSettled > EPS && collectTarget > EPS;
  // Exchange where the returned goods FULLY pay for the new sale (sold ≤
  // returned, but there IS a sale) — the even swap (net == 0) and the refund-net
  // swap (returned > sold). The cashier collects NOTHING on the sale leg; the
  // sale is paid entirely by the return credit, and any excess return is handed
  // back. Distinct from a pure return, which has no sale line at all.
  const saleCoveredByReturn = hasReturns && collectTarget > EPS && netSettled <= EPS;
  // The returned value credited onto the sale leg so Σ sale payments ==
  // saleTotal while the cashier physically tenders only the net. A positive
  // exchange credits the full return (net + returnTotal == saleTotal); a
  // covered sale credits just the sale total (the rest of the return is
  // physically refunded).
  const returnCredit = isPositiveExchange
    ? returnTotal
    : saleCoveredByReturn
      ? collectTarget
      : 0;
  // Existing splits math keys off `orderTotal` — the amount the cashier
  // physically collects on the sale leg: the net for a positive exchange, zero
  // when the return covers the sale, else the full sale total. handleConfirm
  // re-grosses the sale leg back to `collectTarget` via `returnCredit`.
  const orderTotal = isPositiveExchange
    ? roundM(netSettled)
    : saleCoveredByReturn
      ? 0
      : collectTarget;
  // A fully-discounted sale (100% off) OR a pure return nets the SALE leg to
  // zero. There's nothing to collect, but a plain sale still needs a payment
  // method on record (recorded at amount 0). A pure return skips the sale
  // splits entirely (refund method only).
  const isZeroOrder = collectTarget <= EPS && !hasReturns;

  // A pure refund (all-return cart): there's nothing to collect, so the
  // sale-collect UI is hidden and only the refund method picker is shown.
  const pureReturn = hasReturns && collectTarget <= EPS;
  // Cases where the cashier collects nothing on the sale leg: a pure return, or
  // an exchange whose sale is fully paid by the returned goods. In both the
  // sale amount/method/splits UI is hidden and confirm needs no sale split.
  const noSaleCollect = pureReturn || saleCoveredByReturn;
  // What physically goes back to the customer: the whole return for a pure
  // return, or just the net excess for a covered-sale exchange (0 for an even
  // swap). Drives the balance panel and refund picker label.
  const netRefundToCustomer = pureReturn
    ? returnTotal
    : saleCoveredByReturn
      ? roundM(Math.abs(netSettled))
      : 0;

  // Refund method for the return leg (single method; any sale method works).
  const [refundCode, setRefundCode] = useState<number | null>(null);

  /**
   * The header discount expressed as a flat EGP amount.
   *
   * The cart's `discount` shape can be either:
   *   - { type: 'amount', value: 20 }    → 20 EGP off
   *   - { type: 'percent', value: 15 }   → 15% off
   *
   * `buildCreateOrderRequest` only accepts `headerDiscountValue` (an
   * absolute amount) and forwards it as `discount_value` on the wire.
   * If we passed `headerDiscountValue` only when type==='amount', a
   * percent discount would silently drop here — the server would
   * compute totals WITHOUT the discount, expected_total wouldn't match,
   * and the order would 422 with TOTALS_MISMATCH.
   *
   * The cart's totals() selector already computes the absolute discount
   * amount for both types (capping at subtotal), so we just read it
   * from there. This keeps the backend's discount_value aligned with
   * what the cart UI is showing.
   */
  const headerDiscountValue = roundM(t.discount);

  /**
   * Loyalty redemption, if the cashier applied one. Only the POINTS go on the
   * wire — the server re-prices them — but the value is included in the
   * `expected_*` totals so a client/server disagreement surfaces as the usual
   * TOTALS_MISMATCH rather than a quietly wrong charge.
   */
  const loyaltyRedeem = useCart((s) => s.loyaltyRedeem);
  const loyaltyArgs =
    loyaltyRedeem && loyaltyRedeem.points > 0
      ? {
          loyaltyRedeemPoints: loyaltyRedeem.points,
          loyaltyRedeemValue: roundM(t.loyaltyDiscount),
          ...(loyaltyRedeem.asPayment ? { loyaltyAsPayment: true } : {}),
        }
      : {};

  /**
   * Gift cards.
   *
   * A gift card is money, not a discount, so it settles as a real payment
   * split — but every card shares one tender code, and the methods grid is
   * one-row-per-code. So the applied cards live in their own state here and are
   * collapsed into a SINGLE split on the gift-card code when the order is
   * built; the individual cards ride along in `gift_cards.redeem[]`, and the
   * server refuses the order unless the two add up to the same figure.
   */
  const giftCardConfig = useGiftCards((s) => s.config);
  const giftCardCode = giftCardConfig.payment_type_code;
  const setIssuedGiftCards = useGiftCards((s) => s.setIssued);
  const [giftCards, setGiftCards] = useState<AppliedGiftCard[]>([]);
  const [giftCardRefund, setGiftCardRefund] = useState<GiftCardRefundChoice | null>(null);
  const giftCardTotal = roundM(giftCards.reduce((s, g) => s + g.amount, 0));

  // Live payment-types — warmed at login (usePaymentTypesWarm in LegacyApp) so
  // this reads from cache instantly on first open. Same query key, so the two
  // share one cache entry. Tenant ops change these in the back-office, not
  // mid-shift, so the long staleTime is safe.
  const { data: paymentTypesData, isLoading: typesLoading } = useQuery({
    queryKey: PAYMENT_TYPES_QUERY_KEY,
    queryFn: () => paymentAPI.listTypes(),
    staleTime: 10 * 60 * 1000, // 10 min
    // Gated to open, but the warm-up already populated this exact cache key at
    // login — a disabled query still returns cached data, so the grid shows
    // instantly on open with no fetch (and we never fetch before auth).
    enabled: isOpen,
  });
  const allTypes = paymentTypesData?.payment_types ?? [];
  // Three-way split of payment types:
  //
  //   methods    → buttons in the grid. Excludes the sentinel codes
  //                (-1 Unpaid, 0 Multi). "Credit" (Code 2) IS a grid method
  //                since 2026-09-09: a tenant that flags it IsSales/Is_POS
  //                uses it exactly like Cash or a card, and one that doesn't
  //                never sees it. Gateway codes (-3 Tap, -4 UPayments,
  //                -5 Tabby, -6 Tamara, -7 MyFatoorah) ARE surfaced here
  //                despite being negative — they open GatewayPaymentDialog
  //                instead of adding a split (handled by the gateway branch
  //                in handleAddSplit below).
  //   unpaidType → Code = -1. The on-account tender for BOTH customer
  //                kinds: a cash customer leaves a Code 51 receipt unpaid
  //                against their cash-customer balance; a credit (GL
  //                account) customer gets a Code 52 credit-sales invoice on
  //                their account — the server decides from customer_type.
  //   giftCard   → excluded from the grid entirely and handled by
  //                GiftCardPayPanel: picking it as a plain method would
  //                register gift-card tender with no card behind it, which
  //                the server rejects (GIFT_CARD_TENDER_UNBACKED).
  const methods = allTypes.filter(
    (t) =>
      (t.code > 0 || isPaymentGatewayCode(t.code)) &&
      t.code !== giftCardCode,
  );
  const unpaidType = allTypes.find((t) => t.code === -1) ?? null;

  // Customer-driven CTA selection:
  //   - cash customer attached → show "Unpaid" (red, header)
  //   - credit customer attached → show "On Account" (red, header)
  //   - no customer → neither CTA
  // Both post the single Unpaid tender; only the wording differs.
  const customerKind = customer?.type ?? null;
  // A HELD invoice is already unpaid — this modal is here to pay it, and the
  // held-order pay path refuses the Unpaid tender anyway.
  const payingHeld = useCart((st) => st.editingHeld);
  const headerCta: HeaderCta | null = (() => {
    if (payingHeld) return null;
    if (!customerKind || !unpaidType) return null;
    if (customerKind === 'cash') {
      return { kind: 'unpaid', label: 'Unpaid', type: unpaidType };
    }
    if (customerKind === 'credit') {
      return { kind: 'on-account', label: 'On Account', type: unpaidType };
    }
    return null;
  })();

  // ── Split state ──────────────────────────────────────────────────────
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');
  const [splits, setSplits] = useState<Split[]>([]);
  const [amountStr, setAmountStr] = useState<string>('');
  // Set when the cashier picks a gateway method — opens GatewayPaymentDialog
  // with this exact draft. Cleared on cancel / success.
  const [gatewayDraft, setGatewayDraft] = useState<{ gateway: PaymentGatewayCode; request: CreateOrderRequest } | null>(null);

  // ── KNET card terminal (KNET_TERMINAL.md) ─────────────────────────────
  // Only for a drawer whose admin switched the integration on; for every other
  // drawer `knetCfg.enabled` is false and nothing below changes a thing.
  const knetCfg = useKnetTerminalConfig(
    cashier ? `${cashier.tenant_db ?? ''}:${cashier.cash_drawer_id ?? ''}` : null,
  );
  // The terminal dialog in progress: a sale slice, or the refund leg.
  const [knetDraft, setKnetDraft] = useState<
    | { mode: 'purchase'; type: PaymentType; amount: number }
    | { mode: 'refund'; type: PaymentType; amount: number; parentReceiptId: number }
    | null
  >(null);
  // An approved terminal REFUND for the return leg, waiting for Confirm.
  const [knetRefund, setKnetRefund] = useState<KnetTerminalTxn | null>(null);
  // Attempts that went out on an order request — never auto-reversed.
  const submittedKnetIds = useRef<Set<number>>(new Set());
  const splitsRef = useRef<Split[]>([]);
  splitsRef.current = splits;
  const knetRefundRef = useRef<KnetTerminalTxn | null>(null);
  knetRefundRef.current = knetRefund;
  // Confirm was pressed and is waiting on the terminal refund.
  const confirmAfterRefundRef = useRef(false);
  // The terminal refund failed and the cashier chose to refund manually.
  const refundManualRef = useRef(false);
  // Bumped to resume Confirm once the terminal refund settled.
  const [resumeConfirm, setResumeConfirm] = useState(0);

  // Drop the previous sale's tender the moment the modal CLOSES.
  //
  // The reset below runs on open, which means the first render of a reopened
  // modal still holds the LAST order's splits: a leftover 20.000 cash split
  // against a new 5.000 basket reads as "balance covered", so the Applied list
  // flashes the old payments for a frame and the amount box renders DISABLED.
  // Clearing on the way out makes the reopened modal correct from its very
  // first frame instead of one commit later.
  useEffect(() => {
    if (isOpen) return;
    // A card approved on the terminal but never put on an order is reversed,
    // so closing the modal can never leave a customer charged for nothing.
    const refundId = knetRefundRef.current?.id;
    const orphans = unusedApprovals(
      refundId != null ? [...splitsRef.current, { knetTxnId: refundId }] : splitsRef.current,
      submittedKnetIds.current,
    );
    for (const id of orphans) void knetTerminalAPI.reverse(id).catch(() => undefined);
    submittedKnetIds.current = new Set();
    setSplits([]);
    setGiftCards([]);
    setGiftCardRefund(null);
    setKnetDraft(null);
    setKnetRefund(null);
  }, [isOpen]);

  // Reset everything when the modal reopens (cart could have changed).
  useEffect(() => {
    if (!isOpen) return;
    setIdempotencyKey(generateUuid());
    setSplits([]);
    setGiftCards([]);
    setGiftCardRefund(null);
    setAmountStr(amountText(orderTotal));
    setGatewayDraft(null);
    setKnetDraft(null);
    setKnetRefund(null);
    confirmAfterRefundRef.current = false;
    refundManualRef.current = false;
    // Default the refund to a cash method when one exists, else the first.
    const cashMethod = methods.find((m) => m.is_cash) ?? methods[0] ?? null;
    setRefundCode(cashMethod ? cashMethod.code : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Gift cards are tender like any other, so they count towards what has been
  // collected — they are simply registered through their own panel rather than
  // through the methods grid.
  const splitSum = roundM(splits.reduce((s, p) => s + p.amount, 0) + giftCardTotal);
  const remaining = roundM(orderTotal - splitSum);

  // After every add/remove split — and whenever the amount owed itself moves,
  // e.g. loyalty points applied or removed from the panel below — refill the
  // input with the new remaining balance. This deliberately depends on
  // splits.length (which changes only on add/remove) and orderTotal, NOT
  // amountStr (which would create a feedback loop on every keystroke).
  // Without orderTotal here, redeeming points left the input on the
  // pre-redemption figure until the modal was closed and reopened.
  useEffect(() => {
    if (!isOpen) return;
    setAmountStr(amountText(remaining > 0 ? remaining : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splits.length, giftCards.length, orderTotal, isOpen]);

  const enteredAmount = roundM(parseFloat(amountStr) || 0);

  /**
   * Focus the amount box on open AND select what is in it.
   *
   * The box opens pre-filled with the whole order total, so a cashier who
   * simply typed the figure on the note the customer handed over was
   * APPENDING to it — 1.500 then "20" gives "1.50020" — and had to clear the
   * field by hand first. Selecting makes the first keystroke replace it, which
   * is what the pre-fill was always meant to allow.
   *
   * THE FIRST ATTEMPT IS SYNCHRONOUS, and that is the whole lesson here.
   * An earlier version of this dropped the input's `autoFocus` and moved the
   * focus into a requestAnimationFrame callback — which lands OUTSIDE the
   * window of the click that opened the modal. A browser may refuse
   * programmatic focus there (Chrome on Android does, rather than raise a
   * keyboard with no gesture behind it), so the box came up with neither
   * focus nor selection: worse than the plain `autoFocus` it replaced.
   * `autoFocus` is therefore back — it focuses inside the gesture, during the
   * commit — and this effect, which React runs in that same task for a
   * discrete event, only has to add the selection.
   *
   * The frame retries stay as a fallback for the case the synchronous pass
   * cannot serve: a DISABLED input takes no focus, and this one can still be
   * disabled on the first frame when a sibling effect has yet to clear state.
   *
   * Deliberately `isOpen` only. The same value is refilled whenever the
   * balance moves (a split applied, loyalty points redeemed), and grabbing
   * focus on THAT would yank the cashier out of the loyalty or notes field
   * mid-edit. Opening the modal is the one moment the box is unambiguously
   * where they are looking.
   */
  const amountInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    let frame = 0;
    let tries = 0;
    const attempt = (): boolean => {
      const el = amountInputRef.current;
      if (!el || el.disabled) return false;
      el.focus();
      // select() is not defined for every input type in every engine (Firefox
      // treats a number input as having no selection API). Never let that take
      // the focus down with it.
      try {
        el.select();
      } catch {
        /* focused is the half that matters */
      }
      return true;
    };
    const retry = () => {
      // ~10 frames ≈ 160 ms, then stop: a genuinely disabled box (a zero-total
      // order is covered from the start) must not spin for the modal's life.
      if (attempt() || ++tries >= 10) return;
      frame = requestAnimationFrame(retry);
    };
    if (!attempt()) frame = requestAnimationFrame(retry);
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const editingReceiptId = useCart((s) => s.editingReceiptId);

  /**
   * Complete the sale without the server.
   *
   * Same three things a successful charge does, in the same order — print,
   * clear the cart, close the modal — because from the cashier's side of the
   * counter this IS a completed sale. The only differences are the number on
   * the paper, which is provisional, and the row now sitting in the queue.
   *
   * The receipt is printed from the request the charge path already built, so
   * the printed figures are the ones the server will be sent. Nothing is
   * re-derived here.
   */
  async function takeSaleOffline(req: CreateOrderRequest): Promise<void> {
    // A failed charge is the most reliable connectivity signal this app has.
    // `navigator.onLine` reports a link, not a route — on a wifi network whose
    // uplink is dead it stays true — and if we believed it, the promotion
    // quote would keep firing and holding the Charge button for its timeout on
    // every basket. The queue's own retries put this back to true when a real
    // request succeeds.
    useNetwork.getState().setOnline(false);

    const provisionalNo = nextProvisionalNo(cashier?.cash_drawer_id ?? null);
    const capturedAt = req.entry_date ?? new Date().toISOString();

    // The response shape the receipt builder expects, filled from what the
    // till already knows. `entry_no` is 0 because there is no invoice number
    // yet — `displayCode` is what actually gets printed.
    const stand: CreateOrderResponse = {
      order_id: 0,
      entry_no: 0,
      entry_date: capturedAt,
      totals: {
        subtotal: req.expected_subtotal,
        discount_value: req.discount_value ?? 0,
        delivery_fee: req.delivery_fee ?? 0,
        tax_value: req.expected_tax,
        net_value: req.expected_total,
      },
      idempotent_replay: false,
    };

    let receipt = null as ReceiptData | null;
    if (cashier) {
      receipt = printAfterCharge({
        queryClient,
        response: stand,
        request: req,
        cartLines: lines,
        customer,
        table,
        tenantDb: cashier.tenant_db ?? null,
        cashier: cashierIdentity(cashier),
        paymentTypes: allTypes,
        displayCode: provisionalNo,
      });
    }

    await queueSaleOffline({
      request: req,
      receipt,
      provisionalNo,
      drawerId: cashier?.cash_drawer_id ?? null,
      shiftId: cashier?.shift_id ?? null,
      summary: {
        total: req.expected_total,
        itemCount: lines.reduce((n, l) => n + l.qty, 0),
        customerLabel: customer?.name ?? null,
      },
    });

    toast(
      tr({
        en: `Saved offline as ${provisionalNo} · ${fmt(req.expected_total)} — sync when you are back online`,
        ar: `تم الحفظ دون اتصال باسم ${provisionalNo} · ${fmt(req.expected_total)} — زامنه عند عودة الاتصال`,
      }),
      'success',
    );

    reset();
    closeModal();
  }

  /**
   * Charge, or queue it if there is nothing to charge against.
   *
   * The offline path used to be reached only by letting the POST fail, which
   * has two problems the first test found immediately. A till that already
   * knows it is offline still sat through a request that could not succeed;
   * and when the connection dropped mid-flight — the request out, the response
   * lost — axios waited its full 30 s timeout while the button said "Saving…".
   * The server had written the order by then, so the cashier was frozen in
   * front of a customer waiting on a sale that had already succeeded.
   *
   * So: when we know we are offline, do not ask. The browser will not deliver
   * it and the cashier should not wait to be told so.
   */
  function submitOrder(req: CreateOrderRequest): void {
    // A terminal approval that has gone out on an order is never auto-reversed
    // when the modal closes — the server links it, or the admin log flags it.
    for (const p of [...req.payments, ...(req.refund_payments ?? [])]) {
      if (p.knet_terminal_txn_id != null) submittedKnetIds.current.add(p.knet_terminal_txn_id);
    }
    const offline = !useNetwork.getState().online || navigator.onLine === false;
    const offlineEnabled = useTenantConfig.getState().offlineSalesEnabled;
    if (offline && cashier && offlineBlockReason(req, editingReceiptId, offlineEnabled) === null) {
      void takeSaleOffline(req);
      return;
    }
    placeOrder.mutate(req);
  }

  // ── Submit ────────────────────────────────────────────────────────────
  const placeOrder = usePlaceOrder({
    onSuccess: (response) => {
      const replayLabel = response.idempotent_replay
        ? tr({ en: ' (already saved)', ar: ' (محفوظ مسبقًا)' })
        : '';
      toast(
        tr({
          en: `Order #${response.entry_no} saved · ${fmt(response.totals.net_value)}${replayLabel}`,
          ar: `تم حفظ الطلب #${response.entry_no} · ${fmt(response.totals.net_value)}${replayLabel}`,
        }),
        'success',
      );

      // Loyalty outcome: the cashier tells the customer what they spent and
      // what they now have. Only shown when points actually moved, and always
      // taken from the SERVER's numbers, never the ones the cart guessed.
      if (response.loyalty) {
        const l = response.loyalty;
        const parts: Array<{ en: string; ar: string }> = [];
        if (l.redeemed_points > 0) {
          parts.push({
            en: `${l.redeemed_points} pts redeemed (${fmt(l.redeemed_value)})`,
            ar: `تم استبدال ${l.redeemed_points} نقطة (${fmt(l.redeemed_value)})`,
          });
        }
        if (l.earned_points > 0) {
          // A membership package grants its own points on top of the money
          // earn; say so, since the figure can be far larger than the sale.
          const bonus = l.membership_bonus_points ?? 0;
          parts.push(
            bonus > 0
              ? {
                  en: `${l.earned_points} pts earned (${bonus} from membership)`,
                  ar: `تم كسب ${l.earned_points} نقطة (${bonus} من العضوية)`,
                }
              : {
                  en: `${l.earned_points} pts earned`,
                  ar: `تم كسب ${l.earned_points} نقطة`,
                },
          );
        }
        toast(
          tr({
            en: `${parts.map((p) => p.en).join(' · ')} — balance ${l.balance_points} pts`,
            ar: `${parts.map((p) => p.ar).join(' · ')} — الرصيد ${l.balance_points} نقطة`,
          }),
          'info',
        );
        // The cart is about to be reset, but if the same customer is served
        // again the balance on screen must be the post-charge one.
        void useLoyalty.getState().refreshCustomer();
        // The customer picker caches each customer for up to a minute; drop
        // it so the card shows the new balance, not the pre-sale one.
        void queryClient.invalidateQueries({ queryKey: ['customers'] });
      }

      // Gift-card outcome. Redemptions and refunds are just told to the cashier;
      // freshly SOLD cards are handed to the store instead, because their
      // numbers and PINs have to survive this modal closing — the server will
      // never return them again.
      if (response.gift_cards) {
        const g = response.gift_cards;
        for (const r of g.redeemed) {
          toast(
            tr({
              en: `Gift card ${r.card_number_masked}: −${fmt(r.amount)}, ${fmt(r.balance_after)} left`,
              ar: `بطاقة ${r.card_number_masked}: −${fmt(r.amount)}، المتبقي ${fmt(r.balance_after)}`,
            }),
            'info',
          );
        }
        for (const r of g.refunded) {
          toast(
            tr({
              en: `Refunded ${fmt(r.amount)} onto ${r.card_number_masked} — ${fmt(r.balance_after)} on the card`,
              ar: `تم رد ${fmt(r.amount)} إلى ${r.card_number_masked} — الرصيد ${fmt(r.balance_after)}`,
            }),
            'success',
          );
        }
        if (g.issued.length > 0) setIssuedGiftCards(g.issued);
      }

      // Receipt auto-print. Fire-and-forget — errors here do not block
      // the cashier flow; the "Print again" button on the success toast
      // covers any printer hiccup. The print config was warmed at login
      // (App.tsx → usePrintConfig) so this reads from React Query cache,
      // not the network — zero extra round-trips after charge.
      const req = lastReqRef.current;
      if (useCart.getState().laundryPayNow) {
        // The laundry ticket IS the receipt (slip + order barcode, customer
        // copy + bag tag) — printed instead of, not as well as, the plain one.
        void printLaundryTicket(response.order_id);
      } else if (req && cashier) {
        printAfterCharge({
          queryClient,
          response,
          request: req,
          cartLines: lines,
          customer: printCustomer,
          table,
          tenantDb: cashier.tenant_db ?? null,
          cashier: cashierIdentity(cashier),
          paymentTypes: allTypes,
        });
      }

      reset();
      closeModal();
    },
    onError: (err) => {
      // The only path into the offline queue.
      //
      // A network error means no HTTP response came back at all — the request
      // never completed a round trip. A 4xx or a 5xx did reach the server and
      // is handled exactly as it always has been: the server heard it, it may
      // have written something, and replaying that blindly from a queue is how
      // a sale gets posted from a state nobody looked at.
      if (isNetworkError(err)) {
        const req = lastReqRef.current;
        const offlineEnabled = useTenantConfig.getState().offlineSalesEnabled;
        const blocked = req
          ? offlineBlockReason(req, editingReceiptId, offlineEnabled)
          : 'NO_REQUEST';
        if (req && cashier && blocked === null) {
          void takeSaleOffline(req);
          return;
        }
        // Offline, but this basket cannot be queued safely — see
        // offlineBlockReason. Say which part is the problem rather than
        // showing a bare network error the cashier can do nothing with.
        toast(
          blocked === 'DISABLED'
            ? tr({
                en: 'No connection. Offline sales are switched off for this company, so this sale cannot be saved until you are back online.',
                ar: 'لا يوجد اتصال. البيع دون اتصال معطّل لهذه الشركة، لذلك لا يمكن حفظ هذه العملية حتى يعود الاتصال.',
              })
            : blocked && blocked !== 'NO_REQUEST'
              ? tr({
                  en: 'No connection — and this sale needs it. Try again once you are back online.',
                  ar: 'لا يوجد اتصال — وهذه العملية تحتاجه. حاول مرة أخرى بعد عودة الاتصال.',
                })
              : tr({
                  en: 'No connection. Try again once you are back online.',
                  ar: 'لا يوجد اتصال. حاول مرة أخرى بعد عودة الاتصال.',
                }),
          'error',
        );
        return;
      }
      // The tenant blocks selling below stock and another till got there
      // first (the cart's own check passed a moment ago). The server names
      // every short item; show them in the cashier's language instead of the
      // English 422 text.
      if (getApiErrorCode(err) === 'INSUFFICIENT_STOCK') {
        const details = getApiErrorDetails<{ shortages?: StockShortageDto[] }>(err);
        const shortages = details?.shortages ?? [];
        if (shortages.length > 0) {
          useStockLevels.getState().openDialog(shortages, true);
          useStockLevels.setState({ shortages, signature: '' });
        }
        toast(
          tr({
            en: 'Not saved: some items exceed the stock on hand',
            ar: 'لم يُحفظ: بعض الأصناف تتجاوز الرصيد المتاح',
          }),
          'error',
        );
        return;
      }
      toast(getApiErrorMessage(err) || tr({ en: 'Failed to save order', ar: 'فشل حفظ الطلب' }), 'error');
    },
  });

  // The legacy order_type this sale is booked under — the same helper the cart
  // quotes promotions with, so the preview and the charge cannot disagree.
  //
  // This used to be spelled out inline and got dine-in wrong: it tested the
  // legacy `orderType` tag (only ever 'walkin' or 'delivery') instead of
  // `orderMode`, so a dine-in sale was booked as 1, take-away. A promotion
  // scoped to Dine-in could therefore never fire from the till.
  const orderTypeCode = useMemo(
    () => orderTypeCodeFor(orderMode, orderType),
    [orderMode, orderType],
  );

  // Confirm enabled rules:
  //   - at least one split
  //   - Σ splits ≥ orderTotal (within EPS)
  //   - if Σ > orderTotal, the OVERAGE must come from a cash split
  //     (we trim the last cash split when submitting; non-cash overage
  //     would orphan money on a card and is blocked at registration)
  const balanceCovered = splitSum >= orderTotal - EPS;
  const overTenderAmount = roundM(Math.max(0, splitSum - orderTotal));
  const lastSplitIsCash =
    splits.length > 0 && splits[splits.length - 1]!.isCash;
  const overTenderAllowed = overTenderAmount <= EPS || lastSplitIsCash;
  // ── Change due ────────────────────────────────────────────────────────
  //
  // Shown only once the money is actually APPLIED, and only when the tender
  // carrying the overage is CASH. Both halves are deliberate:
  //
  //   applied — an earlier version previewed the change as the amount was
  //             typed, before any method was picked. It is off the table: a
  //             figure the cashier has not committed to is a promise about a
  //             tender that may still turn out to be a card, and the drawer is
  //             counted against what was applied, not what was typed.
  //   cash    — this is the only tender the overage can be handed back from.
  //             A card or gateway split is clamped to the remaining balance at
  //             click time, and a gift card's overage is not cash in a drawer.
  //             `overTenderAllowed` already refuses to submit a non-cash
  //             overage; this makes the panel agree with it rather than
  //             offering change against money that cannot be given back.
  //
  // This is the same figure that rides onto the receipt as `change_due`.
  const showChangeDue = balanceCovered && overTenderAmount > EPS && lastSplitIsCash;
  // The other side of that rule: an overage sitting on a CARD. Confirm is
  // already blocked (`overTenderAllowed`), but with no change panel and no
  // balance due the footer would otherwise just be a dead button. Say what is
  // wrong instead of reporting the order as fully paid.
  const blockedOverTender = balanceCovered && overTenderAmount > EPS && !lastSplitIsCash;
  // The sale leg needs at least one split when there's something to collect;
  // a pure return (collectTarget ~0) needs none. The refund leg needs a
  // method chosen when there's something to give back.
  const saleSideOk =
    collectTarget <= EPS || saleCoveredByReturn
      ? true
      : splits.length + giftCards.length > 0 && balanceCovered && overTenderAllowed;
  // A refund onto a gift card needs the card as well as the method — the server
  // rejects gift-card refund tender that names no card.
  const refundSideOk =
    returnTotal <= EPS
      ? true
      : refundCode != null &&
        (refundCode !== giftCardCode || giftCardRefund != null);
  const canConfirm = saleSideOk && refundSideOk && !placeOrder.isPending;

  function handleAddSplit(type: PaymentType) {
    if (placeOrder.isPending) return;
    // POS_CreditRequiresCreditCustomer: Credit (Code 2) is only the credit
    // invoice — alone, on a sale, for a credit customer. The server refuses
    // anything else; this says why before the cashier gets that far.
    if (type.code === 2 && useTenantConfig.getState().creditRequiresCreditCustomer) {
      if (customerKind !== 'credit') {
        toast(tr({
          en: 'Credit needs a credit customer — attach the customer first',
          ar: 'الدفع الآجل يتطلب عميل آجل — اختر العميل أولاً',
        }), 'warning');
        return;
      }
      if (payingHeld || hasReturns || splits.length > 0) {
        toast(tr({
          en: 'Credit must be the only payment on a sale',
          ar: 'الدفع الآجل يجب أن يكون طريقة الدفع الوحيدة على فاتورة بيع',
        }), 'warning');
        return;
      }
    }
    // Credit already chosen under the same setting: nothing may join it.
    if (
      type.code !== 2 &&
      splits.some((s) => s.code === 2) &&
      useTenantConfig.getState().creditRequiresCreditCustomer
    ) {
      toast(tr({
        en: 'Credit must be the only payment — remove it to pay another way',
        ar: 'الدفع الآجل يجب أن يكون طريقة الدفع الوحيدة — أزله للدفع بطريقة أخرى',
      }), 'warning');
      return;
    }
    // A hosted gateway takes the full order in one charge, exclusive with
    // other splits (same idea as Unpaid). We open GatewayPaymentDialog instead
    // of adding a split — the order is finalized only after the provider
    // reports the money captured.
    const gateway = paymentGatewayForCode(type.code);
    if (gateway) {
      if (!cashier) return;
      const gwName = lang === 'ar' ? gateway.nameAr : gateway.nameEn;
      if (hasReturns) {
        toast(tr({ en: `${gwName} is not available for returns/exchanges`, ar: `${gwName} غير متاح للمرتجعات/الاستبدال` }), 'info');
        return;
      }
      if (splits.length > 0) {
        toast(tr({ en: `Remove other applied payments first to use ${gwName}`, ar: `أزل المدفوعات المطبقة الأخرى أولاً لاستخدام ${gwName}` }), 'info');
        return;
      }
      const req = buildCreateOrderRequest({
        idempotencyKey,
        branchId: cashier.branch_id,
        cartLines: lines,
        customer,
        customerAddressId,
        payments: [{ type_code: type.code, amount: orderTotal }],
        ...(headerDiscountValue > 0 ? { headerDiscountValue } : {}),
      ...loyaltyArgs,
        ...deliveryArgs,
        orderTypeCode,
        ...(discount?.discountReasonId != null
          ? { discountReasonId: discount.discountReasonId }
          : {}),
        ...(overrideUserId != null ? { managerOverrideUserId: overrideUserId } : {}),
        // A coupon priced by the NEW engine travels as a CODE (the server
      // re-validates and prices it); the legacy field carries the old
      // GL_Coupon_* path. Never both — that would discount twice.
      ...(discount?.couponCode
        ? discount.serverPriced
          ? { engineCouponCode: discount.couponCode }
          : { couponCode: discount.couponCode }
        : {}),
        ...(table ? { table } : {}),
      });
      lastReqRef.current = req;
      setGatewayDraft({ gateway: gateway.code, request: req });
      return;
    }
    // Zero-net order (full discount): record the chosen method at amount 0.
    // Replace any existing pick so only one method ends up on the receipt.
    if (isZeroOrder) {
      setSplits([
        { code: type.code, name: type.name, amount: 0, isCash: type.is_cash },
      ]);
      return;
    }
    if (enteredAmount <= 0) {
      toast(tr({ en: 'Enter an amount first', ar: 'أدخل مبلغًا أولاً' }), 'warning');
      return;
    }
    if (splits.some((s) => s.code === type.code)) {
      toast(tr({
        en: `${type.name} already added — remove it to change`,
        ar: `${type.name} مضاف بالفعل — أزله للتغيير`,
      }), 'info');
      return;
    }
    let amount = enteredAmount;
    if (!type.is_cash && amount > remaining + EPS) {
      amount = remaining;
      toast(tr({
        en: `${type.name} clamped to remaining ${fmt(remaining)}`,
        ar: `تم ضبط ${type.name} على المتبقي ${fmt(remaining)}`,
      }), 'info');
    }
    if (amount <= 0) {
      toast(tr({ en: 'Order is already covered', ar: 'الطلب مغطى بالفعل' }), 'info');
      return;
    }
    // KNET card terminal: on a drawer where this tender drives the terminal,
    // the slice is added only once the terminal APPROVES. Exchanges keep the
    // manual tender (their sale leg is grossed up with the return credit, so
    // the slice is not the amount the card pays).
    if (isTerminalTender(knetCfg, type.code) && !hasReturns) {
      setKnetDraft({ mode: 'purchase', type, amount: roundM(amount) });
      return;
    }
    setSplits((prev) => [
      ...prev,
      { code: type.code, name: type.name, amount: roundM(amount), isCash: type.is_cash },
    ]);
  }

  /** The terminal approved: the slice carries the attempt id to the order. */
  function handleKnetApproved(txn: KnetTerminalTxn) {
    const draft = knetDraft;
    setKnetDraft(null);
    if (!draft) return;
    if (draft.mode === 'refund') {
      setKnetRefund(txn);
      if (confirmAfterRefundRef.current) setResumeConfirm((n) => n + 1);
      return;
    }
    setSplits((prev) => [
      ...prev,
      {
        code: draft.type.code,
        name: draft.type.name,
        amount: roundM(txn.amount),
        isCash: false,
        knetTxnId: txn.id,
        knetLabel: cardSummary(txn),
      },
    ]);
  }

  /** The admin allows it and the terminal failed: record the tender as today. */
  function handleKnetManual() {
    const draft = knetDraft;
    setKnetDraft(null);
    if (!draft) return;
    if (draft.mode === 'refund') {
      refundManualRef.current = true;
      if (confirmAfterRefundRef.current) setResumeConfirm((n) => n + 1);
      return;
    }
    setSplits((prev) => [
      ...prev,
      { code: draft.type.code, name: draft.type.name, amount: draft.amount, isCash: draft.type.is_cash },
    ]);
  }

  /**
   * Removing a slice the terminal already charged reverses the card first —
   * the slice only leaves the list once the reversal is queued.
   */
  async function reverseKnetSplits(toRemove: Split[]): Promise<boolean> {
    const charged = toRemove.filter((s) => s.knetTxnId != null);
    if (charged.length === 0) return true;
    const ok = window.confirm(
      tr({
        en: 'This card payment was already approved on the KNET terminal. Remove it and reverse the charge?',
        ar: 'تمت الموافقة على هذه الدفعة على جهاز كي نت. هل تريد إزالتها وعكس العملية؟',
      }),
    );
    if (!ok) return false;
    try {
      for (const s of charged) await knetTerminalAPI.reverse(s.knetTxnId!);
      toast(tr({ en: 'Reversal sent to the terminal', ar: 'تم إرسال العكس إلى الجهاز' }), 'info');
      return true;
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
      return false;
    }
  }

  async function handleRemoveSplit(code: number) {
    if (placeOrder.isPending) return;
    const target = splits.filter((s) => s.code === code);
    if (!(await reverseKnetSplits(target))) return;
    setSplits((prev) => prev.filter((s) => s.code !== code));
  }

  async function handleClearAll() {
    if (placeOrder.isPending) return;
    if (!(await reverseKnetSplits(splits))) return;
    setSplits([]);
  }

  /**
   * Single-payment shortcut for Unpaid / On Account.
   *
   * Used by both:
   *   - Cash customers → Unpaid sentinel (Code = -1) on a Code 51 receipt.
   *     Backend skips the Inv_Receipt_Payment INSERTs.
   *   - Credit customers → the SAME Unpaid tender, but the server (told by
   *     customer_type: 'credit') writes a Code 52 credit-sales invoice with
   *     the customer's GL account in Vendor_ID — the document the ERP's own
   *     posting procedure debits to the customer's account.
   *
   * Both flows require an attached customer of the matching type and
   * are mutually exclusive with regular splits — caller should hide
   * this CTA if either condition fails.
   */
  function handleOnAccount(type: PaymentType) {
    if (!cashier || !customer || placeOrder.isPending) return;
    const isUnpaid = customer.type !== 'credit';
    const customerName = customer.name || tr({ en: 'this customer', ar: 'هذا العميل' });
    const ok = window.confirm(
      isUnpaid
        ? tr({
            en: `Save ${fmt(orderTotal)} unpaid for ${customerName}?`,
            ar: `حفظ ${fmt(orderTotal)} كغير مدفوع لـ ${customerName}؟`,
          })
        : tr({
            en: `Save ${fmt(orderTotal)} on the customer's account for ${customerName}?`,
            ar: `حفظ ${fmt(orderTotal)} على حساب العميل لـ ${customerName}؟`,
          }),
    );
    if (!ok) return;

    const req = buildCreateOrderRequest({
      idempotencyKey,
      branchId: cashier.branch_id,
      cartLines: lines,
      customer,
      customerAddressId,
      payments: [{ type_code: type.code, amount: orderTotal }],
      // Always send headerDiscountValue when the cart has any discount
      // (covers both percent and amount). See the comment at the top
      // of this file for why we read from totals() rather than
      // discount.value directly.
      ...(headerDiscountValue > 0 ? { headerDiscountValue } : {}),
      ...loyaltyArgs,
      ...deliveryArgs,
      orderTypeCode,
      // Phase 8 audit fields — forward whatever the Discount modal
      // stamped on the cart's discount. Server validates the manager
      // override (defense in depth) and stamps Inv_Receipt_h columns.
      ...(discount?.discountReasonId != null
        ? { discountReasonId: discount.discountReasonId }
        : {}),
      ...(overrideUserId != null ? { managerOverrideUserId: overrideUserId } : {}),
      // A coupon priced by the NEW engine travels as a CODE (the server
      // re-validates and prices it); the legacy field carries the old
      // GL_Coupon_* path. Never both — that would discount twice.
      ...(discount?.couponCode
        ? discount.serverPriced
          ? { engineCouponCode: discount.couponCode }
          : { couponCode: discount.couponCode }
        : {}),
      ...(table ? { table } : {}),
    });
    lastReqRef.current = req;
    submitOrder(req);
  }

  function handleConfirm() {
    if (!cashier || !canConfirm) return;
    // The server requires Σ payments to equal the order net within 0.01.
    // For cash over-tender, we trim the last cash split down to make Σ
    // match exactly, and pass the actual cash tendered separately so the
    // receipt records change due.
    // Gift-card tender is exact by construction (the panel clamps it to both the
    // card balance and the remaining balance), so the over-tender trim only ever
    // applies to what is left for the ordinary methods to cover.
    const exactSplits = computeExactSplits(splits, roundM(orderTotal - giftCardTotal));
    const cashTenderedTotal = roundM(
      splits.filter((s) => s.isCash).reduce((sum, s) => sum + s.amount, 0),
    );

    // Sale-leg payments. The cashier's splits cover `orderTotal` — the net for a
    // positive exchange, zero when the return covers the sale, else the full sale
    // total. In both exchange cases we credit the returned value back onto the
    // sale via the refund method and merge same-method rows, so Σ sale payments
    // equals the full sale total the server expects while the cashier physically
    // tendered only the net (positive) or nothing (covered). `returnCredit` is 0
    // for plain sales and pure returns, so this is a no-op there.
    let salePayments: Array<{ type_code: number; amount: number; knet_terminal_txn_id?: number }> =
      exactSplits.map((s) =>
        s.knetTxnId != null
          ? { type_code: s.code, amount: s.amount, knet_terminal_txn_id: s.knetTxnId }
          : { type_code: s.code, amount: s.amount },
      );
    // Every applied card collapses into ONE split on the gift-card code. The
    // cards themselves travel in `gift_cards.redeem[]`; the server checks the
    // two sums match before it debits anything.
    if (giftCardTotal > EPS && giftCardCode != null) {
      salePayments = [...salePayments, { type_code: giftCardCode, amount: giftCardTotal }];
    }
    if (returnCredit > EPS && refundCode != null) {
      salePayments = [...salePayments, { type_code: refundCode, amount: returnCredit }];
    }
    // Collapse duplicate codes into one row per method. A no-op when the codes
    // are already unique, which is the common case.
    salePayments = mergePayments(salePayments);

    // Refund leg (return/exchange): one split on the chosen refund method
    // for the full returned value. Any sale method is a valid refund method.
    // KNET card terminal: a pure return refunded on the terminal-driven tender
    // goes through the terminal first (when the admin enabled refunds there
    // and the return names its original receipt); Confirm resumes on approval.
    const refundTerminalType =
      pureReturn &&
      refundCode != null &&
      returnTotal > EPS &&
      exchangeParentId != null &&
      knetCfg.refunds_enabled &&
      isTerminalTender(knetCfg, refundCode) &&
      !refundManualRef.current
        ? methods.find((m) => m.code === refundCode) ?? null
        : null;
    if (refundTerminalType && exchangeParentId != null && !knetRefund) {
      confirmAfterRefundRef.current = true;
      setKnetDraft({ mode: 'refund', type: refundTerminalType, amount: returnTotal, parentReceiptId: exchangeParentId });
      return;
    }
    const refundPayments: Array<{ type_code: number; amount: number; knet_terminal_txn_id?: number }> =
      returnTotal > EPS && refundCode != null
        ? [
            knetRefund && refundTerminalType && Math.abs(knetRefund.amount - returnTotal) <= EPS
              ? { type_code: refundCode, amount: returnTotal, knet_terminal_txn_id: knetRefund.id }
              : { type_code: refundCode, amount: returnTotal },
          ]
        : [];

    // Cash tendered / change. For a positive exchange over-tender is entered
    // against the net, but the sale leg is booked at the full sale total, so we
    // add the credited return value back — the server derives change as
    // cash_tendered − Σ sale payments, keeping it equal to the net over-tender.
    const cashTenderedValue = isPositiveExchange
      ? roundM(collectTarget + overTenderAmount)
      : cashTenderedTotal;

    const req = buildCreateOrderRequest({
      idempotencyKey,
      branchId: cashier.branch_id,
      cartLines: lines,
      customer,
      customerAddressId,
      payments: salePayments,
      ...(refundPayments.length > 0 ? { refundPayments } : {}),
      ...(exchangeParentId != null ? { parentReceiptId: exchangeParentId } : {}),
      // Only include cash_tendered when there's overage; otherwise it
      // equals paymentSum and the server defaults that anyway.
      ...(overTenderAmount > EPS ? { cashTendered: cashTenderedValue } : {}),
      // Always send headerDiscountValue when the cart has any discount
      // (covers both percent and amount). See the comment at the top
      // of this file for why we read from totals() rather than
      // discount.value directly.
      ...(headerDiscountValue > 0 ? { headerDiscountValue } : {}),
      ...loyaltyArgs,
      ...(giftCards.length > 0
        ? {
            giftCardRedeem: giftCards.map((g) => ({
              card_number: g.cardNumber,
              ...(g.pin ? { pin: g.pin } : {}),
              amount: g.amount,
            })),
          }
        : {}),
      ...(giftCardRefund && returnTotal > EPS
        ? {
            giftCardRefund: [
              { card_number: giftCardRefund.cardNumber, amount: returnTotal },
            ],
          }
        : {}),
      ...deliveryArgs,
      orderTypeCode,
      // Phase 8 audit fields — forward whatever the Discount modal
      // stamped on the cart's discount. Server validates the manager
      // override (defense in depth) and stamps Inv_Receipt_h columns.
      ...(discount?.discountReasonId != null
        ? { discountReasonId: discount.discountReasonId }
        : {}),
      ...(overrideUserId != null ? { managerOverrideUserId: overrideUserId } : {}),
      // A coupon priced by the NEW engine travels as a CODE (the server
      // re-validates and prices it); the legacy field carries the old
      // GL_Coupon_* path. Never both — that would discount twice.
      ...(discount?.couponCode
        ? discount.serverPriced
          ? { engineCouponCode: discount.couponCode }
          : { couponCode: discount.couponCode }
        : {}),
      ...(table ? { table } : {}),
    });
    lastReqRef.current = req;
    submitOrder(req);
  }

  // Resume Confirm once the terminal refund was approved (or the cashier chose
  // to refund manually after it failed).
  useEffect(() => {
    if (resumeConfirm === 0 || !confirmAfterRefundRef.current) return;
    confirmAfterRefundRef.current = false;
    handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeConfirm]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay show modal-overlay-sheet"
      onClick={(e) => {
        if (e.target === e.currentTarget && !placeOrder.isPending) closeModal();
      }}
    >
      <div className="modal pay-modal">
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--success-light)', color: 'var(--success)' }}
          >
            <CheckIcon size={20} />
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{tr({ en: 'Take payment', ar: 'استلام الدفع' })}</div>
            <div className="modal-header-name-ar">
              {lines.length} {lines.length === 1 ? tr({ en: 'item', ar: 'صنف' }) : tr({ en: 'items', ar: 'أصناف' })} · {fmt(orderTotal)}
            </div>
            {membershipDates && (
              <div className="laundry-paynow-note" role="note">
                {tr({ en: 'Membership', ar: 'اشتراك' })} · {tr({ en: 'from', ar: 'من' })} {membershipDates.start} {tr({ en: 'to', ar: 'إلى' })} {membershipDates.end}
              </div>
            )}
            {laundryPayNow && (
              <div className="laundry-paynow-note" role="note">
                {tr({ en: 'Laundry order · paid now · ready by', ar: 'طلب مغسلة · مدفوع الآن · جاهز في' })}{' '}
                {new Date(laundryPayNow.readyBy).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            )}
          </div>
          {/* Header CTA — "Unpaid" for cash customers, "On Account"
              for credit customers. Same wire flow (single payment row),
              different label and code per customer type. Disabled when
              splits already exist (the legacy ERP doesn't support
              mixing paid + unpaid on one receipt). */}
          {!hasReturns && headerCta && (
            <button
              type="button"
              className="pay-unpaid-btn"
              onClick={() => handleOnAccount(headerCta.type)}
              disabled={placeOrder.isPending || splits.length > 0}
              title={
                splits.length > 0
                  ? tr({ en: 'Remove all applied payments first', ar: 'أزل جميع المدفوعات المطبقة أولاً' })
                  : headerCta.kind === 'unpaid'
                    ? tr({ en: "Save this order as unpaid against the customer's account", ar: 'حفظ هذا الطلب كغير مدفوع على حساب العميل' })
                    : tr({ en: "Save this order on the customer's GL account", ar: 'حفظ هذا الطلب على حساب الأستاذ العام للعميل' })
              }
            >
              {headerCta.kind === 'unpaid'
                ? tr({ en: 'Unpaid', ar: 'غير مدفوع' })
                : tr({ en: 'On Account', ar: 'على الحساب' })}
            </button>
          )}
          <button
            className="modal-close"
            onClick={closeModal}
            disabled={placeOrder.isPending}
            aria-label={tr({ en: 'Close', ar: 'إغلاق' })}
            style={{ marginLeft: 8 }}
          >
            ×
          </button>
        </div>

        <div className="pay-grid">
          {/* LEFT: Amount input + payment-method buttons */}
          <div className="pay-left">
            {/* Points come off BEFORE the tender is chosen: redeeming changes
                the amount due, so it has to be settled before the cashier
                starts registering splits. Renders nothing for a walk-in, a
                tenant without loyalty, or a pure refund. */}
            {!pureReturn && <LoyaltyRedeemPanel disabled={placeOrder.isPending || splits.length > 0} />}
            {/* Gift cards come before the methods grid for the same reason
                points do: applying one changes what is left to collect, so it
                has to be settled before the cashier starts registering splits. */}
            {!noSaleCollect && (
              <GiftCardPayPanel
                applied={giftCards}
                remaining={remaining}
                disabled={placeOrder.isPending}
                onApply={(card) => setGiftCards((prev) => [...prev, card])}
                onRemove={(cardNumber) =>
                  setGiftCards((prev) => prev.filter((g) => g.cardNumber !== cardNumber))
                }
              />
            )}
            {pureReturn && (
              <div className="pay-refund-banner">
                {tr({ en: 'Refund only — pick how to hand back', ar: 'استرداد فقط — اختر طريقة إرجاع' })} {fmt(returnTotal)}
              </div>
            )}
            {saleCoveredByReturn && (
              <div className="pay-refund-banner">
                {netRefundToCustomer > EPS
                  ? `${tr({ en: 'Returned goods cover the sale — refund', ar: 'المرتجع يغطي البيع — استرداد' })} ${fmt(netRefundToCustomer)}`
                  : tr({ en: 'Even exchange — nothing to collect or refund', ar: 'استبدال متعادل — لا تحصيل ولا استرداد' })}
              </div>
            )}
            {!noSaleCollect && (
            <div className="pay-amount-row">
              <label htmlFor="pay-amount" className="pay-label">
                {tr({ en: 'Amount to apply', ar: 'المبلغ المطبق' })}
              </label>
              <input
                id="pay-amount"
                ref={amountInputRef}
                type="number"
                inputMode="decimal"
                step={amountStep()}
                min="0"
                className="pay-amount-input"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                // Tapping back in re-selects too — the cashier returns here to
                // replace the figure, never to edit a digit of it. This is the
                // touch case (the till's actual input); a desktop mouse-up
                // collapses the selection to the click point, where it is a
                // harmless no-op.
                onFocus={(e) => { try { e.currentTarget.select(); } catch { /* no selection API */ } }}
                disabled={placeOrder.isPending || balanceCovered}
                // Focus inside the opening gesture. The effect above adds the
                // selection; this is what guarantees the focus itself on a
                // browser that refuses it outside a user gesture.
                autoFocus
              />
            </div>
            )}

            {!noSaleCollect && (
            <div className="pay-chips">
              {quickAmounts(remaining || orderTotal).map((amt) => (
                <button
                  key={amt}
                  type="button"
                  className="pay-chip"
                  onClick={() => setAmountStr(amountText(amt))}
                  disabled={placeOrder.isPending || balanceCovered}
                >
                  {fmt(amt)}
                </button>
              ))}
              <button
                type="button"
                className="pay-chip pay-chip-clear"
                onClick={() => setAmountStr(amountText(0))}
                disabled={placeOrder.isPending || balanceCovered}
              >
                {tr({ en: 'Clear', ar: 'مسح' })}
              </button>
            </div>
            )}

            {!noSaleCollect && <div className="pay-section-title">{hasReturns ? tr({ en: 'Collect on', ar: 'تحصيل على' }) : tr({ en: 'Payment method', ar: 'طريقة الدفع' })}</div>}
            {!noSaleCollect && (
            <div className="pay-methods">
              {typesLoading && <div className="pay-methods-loading">{tr({ en: 'Loading methods…', ar: 'جارٍ تحميل الطرق…' })}</div>}
              {!typesLoading && methods.length === 0 && (
                <div className="pay-methods-empty">
                  {tr({ en: 'No payment methods configured for this branch.', ar: 'لا توجد طرق دفع مهيأة لهذا الفرع.' })}
                </div>
              )}
              {methods.map((type) => {
                const used = splits.some((s) => s.code === type.code);
                // A gateway can't combine with other splits — disable if any split exists.
                const isGateway = isPaymentGatewayCode(type.code);
                const tapDisabled = isGateway && (splits.length > 0 || hasReturns);
                // A gateway doesn't respect the "covered" check (it's a full-amount,
                // server-charged flow), so keep it enabled until splits exist.
                // A zero-net order reads as "covered" from the first render,
                // which would wrongly disable every method. Instead keep the
                // methods open until the cashier has picked one.
                const disabledByCover =
                  isGateway
                    ? false
                    : isZeroOrder
                      ? splits.length > 0
                      : balanceCovered;
                return (
                  <button
                    key={type.id}
                    type="button"
                    className={`pay-method ${type.is_cash ? 'pay-method-cash' : ''} ${used ? 'pay-method-used' : ''
                      }`}
                    onClick={() => handleAddSplit(type)}
                    disabled={used || placeOrder.isPending || disabledByCover || tapDisabled}
                    title={
                      tapDisabled ? tr({ en: `Remove other applied payments first to use ${type.name}`, ar: `أزل المدفوعات المطبقة الأخرى أولاً لاستخدام ${type.name_ar || type.name}` }) : undefined
                    }
                  >
                    <span className="pay-method-name">{type.name}</span>
                    {type.name_ar && type.name_ar !== type.name && (
                      <span className="pay-method-name-ar">{type.name_ar}</span>
                    )}
                  </button>
                );
              })}
            </div>
            )}

            {/* Refund method for the returned items (return/exchange). Any
                sale method is a valid refund tender. */}
            {returnTotal > EPS && (
              <div className="pay-refund">
                <div className="pay-section-title" style={{ marginTop: 14 }}>
                  {saleCoveredByReturn
                    ? netRefundToCustomer > EPS
                      ? `${tr({ en: 'Refund', ar: 'استرداد' })} ${fmt(netRefundToCustomer)} ${tr({ en: 'on', ar: 'على' })}`
                      : tr({ en: 'Record exchange on', ar: 'تسجيل الاستبدال على' })
                    : `${tr({ en: 'Refund', ar: 'استرداد' })} ${fmt(returnTotal)} ${tr({ en: 'on', ar: 'على' })}`}
                </div>
                <div className="pay-methods">
                  {methods
                    .filter((m) => !isPaymentGatewayCode(m.code))
                    .map((type) => (
                      <button
                        key={`refund-${type.id}`}
                        type="button"
                        className={`pay-method ${type.is_cash ? 'pay-method-cash' : ''} ${
                          refundCode === type.code ? 'pay-method-used' : ''
                        }`}
                        onClick={() => {
                          setRefundCode(type.code);
                          // Switching to an ordinary tender means the money is
                          // no longer going back onto a card.
                          setGiftCardRefund(null);
                        }}
                        disabled={placeOrder.isPending}
                      >
                        <span className="pay-method-name">{type.name}</span>
                        {type.name_ar && type.name_ar !== type.name && (
                          <span className="pay-method-name-ar">{type.name_ar}</span>
                        )}
                      </button>
                    ))}
                </div>

                {/* Money that arrived on a gift card should go back onto it
                    rather than out of the drawer. Only offered for a return
                    against the original invoice, and only for a card that can
                    absorb the whole refund. */}
                <GiftCardRefundPanel
                  parentReceiptId={exchangeParentId}
                  returnTotal={returnTotal}
                  selected={giftCardRefund}
                  disabled={placeOrder.isPending}
                  onSelect={(choice) => {
                    setGiftCardRefund(choice);
                    if (choice && giftCardCode != null) setRefundCode(giftCardCode);
                  }}
                />
              </div>
            )}
          </div>

          {/* RIGHT: Order summary + applied splits + balance */}
          <div className="pay-right">
            <div className="pay-summary">
              <div className="pay-summary-row">
                <span>{tr({ en: 'Subtotal', ar: 'المجموع الفرعي' })}</span>
                <span>{fmt(t.subtotal)}</span>
              </div>
              {t.discount > 0 && (
                <div className="pay-summary-row" style={{ color: 'var(--danger)' }}>
                  <span>{tr({ en: 'Discount', ar: 'الخصم' })}</span>
                  <span>−{fmt(t.discount)}</span>
                </div>
              )}
              {t.loyaltyDiscount + t.loyaltyPayment > 0 && (
                <div className="pay-summary-row" style={{ color: 'var(--danger)' }}>
                  <span>
                    {tr({ en: 'Points', ar: 'النقاط' })}
                    {loyaltyRedeem ? ` · ${loyaltyRedeem.points}` : ''}
                  </span>
                  <span>−{fmt(t.loyaltyDiscount + t.loyaltyPayment)}</span>
                </div>
              )}
              {t.deliveryFee > 0 && (
                <div className="pay-summary-row">
                  <span>{tr({ en: 'Delivery', ar: 'توصيل' })}</span>
                  <span>{fmt(t.deliveryFee)}</span>
                </div>
              )}
              {t.vat > 0 && (
                <div className="pay-summary-row">
                  <span>{tr({ en: 'VAT', ar: 'ضريبة القيمة المضافة' })}</span>
                  <span>{fmt(t.vat)}</span>
                </div>
              )}
              {hasReturns ? (
                <>
                  {collectTarget > EPS && (
                    <div className="pay-summary-row">
                      <span>{tr({ en: 'Sold', ar: 'مباع' })}</span>
                      <span>{fmt(collectTarget)}</span>
                    </div>
                  )}
                  <div className="pay-summary-row" style={{ color: 'var(--danger)' }}>
                    <span>{tr({ en: 'Returned', ar: 'مرتجع' })}</span>
                    <span>−{fmt(returnTotal)}</span>
                  </div>
                  <div className="pay-summary-row pay-summary-total">
                    <span>{netSettled >= 0 ? tr({ en: 'Collect', ar: 'تحصيل' }) : tr({ en: 'Refund', ar: 'استرداد' })}</span>
                    <span>{fmt(Math.abs(netSettled))}</span>
                  </div>
                </>
              ) : (
                <div className="pay-summary-row pay-summary-total">
                  <span>{tr({ en: 'Total', ar: 'الإجمالي' })}</span>
                  <span>{fmt(orderTotal)}</span>
                </div>
              )}
            </div>

            {!noSaleCollect && (
            <div className="pay-splits">
              <div className="pay-splits-header">
                <span>{tr({ en: 'Applied', ar: 'المطبق' })}</span>
                {splits.length > 0 && (
                  <button
                    type="button"
                    className="pay-splits-clear"
                    onClick={() => void handleClearAll()}
                    disabled={placeOrder.isPending}
                  >
                    {tr({ en: 'Clear all', ar: 'مسح الكل' })}
                  </button>
                )}
              </div>
              {splits.length === 0 && (
                <div className="pay-splits-empty">{tr({ en: 'No payments applied yet', ar: 'لم يتم تطبيق أي دفعات بعد' })}</div>
              )}
              {splits.map((s) => (
                <div key={s.code} className="pay-split-row">
                  <span className="pay-split-name">
                    {s.name}
                    {s.isCash && <span className="pay-split-cash-badge">{tr({ en: 'cash', ar: 'نقدي' })}</span>}
                    {s.knetLabel && <span className="pay-split-cash-badge" title="KNET terminal">{s.knetLabel}</span>}
                  </span>
                  <span className="pay-split-amount">{fmt(s.amount)}</span>
                  <button
                    type="button"
                    className="pay-split-remove"
                    onClick={() => void handleRemoveSplit(s.code)}
                    disabled={placeOrder.isPending}
                    aria-label={tr({ en: `Remove ${s.name}`, ar: `إزالة ${s.name}` })}
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
            )}

            {!noSaleCollect && (
            <div className="pay-balance">
              {!balanceCovered && (
                <div className="pay-balance-row pay-balance-due">
                  <span>{tr({ en: 'Balance due', ar: 'المبلغ المتبقي' })}</span>
                  <strong>{fmt(remaining)}</strong>
                </div>
              )}
              {showChangeDue && (
                <div className="pay-change-hero" aria-live="polite">
                  <span className="pay-change-hero-label">
                    {tr({ en: 'Change to customer', ar: 'الباقي للعميل' })}
                  </span>
                  <strong className="pay-change-hero-amount">{fmt(overTenderAmount)}</strong>
                </div>
              )}
              {blockedOverTender && (
                <div className="pay-balance-row pay-balance-due">
                  <span>{tr({ en: 'Over-tendered on a non-cash method', ar: 'مبلغ زائد على طريقة دفع غير نقدية' })}</span>
                  <strong>{fmt(overTenderAmount)}</strong>
                </div>
              )}
              {balanceCovered && overTenderAmount <= EPS && (
                <div className="pay-balance-row pay-balance-paid">
                  <CheckIcon size={16} />
                  <span>{tr({ en: 'Order fully paid', ar: 'تم دفع الطلب بالكامل' })}</span>
                </div>
              )}
            </div>
            )}

            {noSaleCollect && (
              <div className="pay-balance">
                {netRefundToCustomer > EPS ? (
                  <div className="pay-balance-row pay-balance-change">
                    <span>{tr({ en: 'Refund to customer', ar: 'استرداد للعميل' })}</span>
                    <strong>{fmt(netRefundToCustomer)}</strong>
                  </div>
                ) : (
                  <div className="pay-balance-row pay-balance-paid">
                    <CheckIcon size={16} />
                    <span>{tr({ en: 'Even exchange — nothing due', ar: 'استبدال متعادل — لا شيء مستحق' })}</span>
                  </div>
                )}
              </div>
            )}

            {/* Reference no. + Notes. Both are written onto the receipt and
                printed on the A4 sheet — المرجع and الملاحظات. Shown on every
                order, including a pure refund: a return is exactly when a
                branch wants to record why. */}
            <div className="pay-notes">
              <label className="pay-label" htmlFor="pay-reference">
                {tr({ en: 'Reference no.', ar: 'المرجع' })}
              </label>
              <input
                id="pay-reference"
                type="text"
                className="pay-note-input"
                maxLength={200}
                value={referenceNo ?? ''}
                onChange={(e) => setReferenceNo(e.target.value || null)}
                disabled={placeOrder.isPending}
                placeholder={tr({ en: 'Quotation / ticket no.', ar: 'رقم عرض السعر / التذكرة' })}
              />
              <label className="pay-label" htmlFor="pay-note">
                {tr({ en: 'Notes', ar: 'الملاحظات' })}
              </label>
              <textarea
                id="pay-note"
                className="pay-note-textarea"
                rows={2}
                maxLength={2000}
                value={orderNote ?? ''}
                onChange={(e) => setOrderNote(e.target.value || null)}
                disabled={placeOrder.isPending}
                placeholder={tr({ en: 'Printed on the invoice', ar: 'تُطبع على الفاتورة' })}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer pay-footer">
          <button
            className="btn pay-cancel-btn"
            onClick={closeModal}
            disabled={placeOrder.isPending}
          >
            {tr({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button
            className="btn btn-primary pay-confirm-btn"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {placeOrder.isPending
              ? tr({ en: 'Saving…', ar: 'جارٍ الحفظ…' })
              : hasReturns
                ? tr({
                    en: `Confirm · ${netSettled >= 0 ? 'Collect' : 'Refund'} ${fmt(Math.abs(netSettled))}`,
                    ar: `تأكيد · ${netSettled >= 0 ? 'تحصيل' : 'استرداد'} ${fmt(Math.abs(netSettled))}`,
                  })
                : tr({ en: `Confirm · ${fmt(orderTotal)}`, ar: `تأكيد · ${fmt(orderTotal)}` })}
          </button>
        </div>
      </div>

      {gatewayDraft && (
        <GatewayPaymentDialog
          gateway={gatewayDraft.gateway}
          orderRequest={gatewayDraft.request}
          paymentTypes={allTypes}
          onSuccess={() => {
            // Cart was already reset inside the dialog after print.
            setGatewayDraft(null);
            closeModal();
          }}
          onCancel={() => {
            // Customer abandoned or charge failed — return to the splits
            // grid so the cashier can pick a different method. Generate a
            // fresh idempotency key since the cart could change next attempt.
            setGatewayDraft(null);
            setIdempotencyKey(generateUuid());
          }}
        />
      )}

      {knetDraft && (
        <KnetTerminalDialog
          mode={knetDraft.mode}
          amount={knetDraft.amount}
          paymentTypeCode={knetDraft.type.code}
          tenderName={knetDraft.type.name}
          orderIdempotencyKey={idempotencyKey || null}
          parentReceiptId={knetDraft.mode === 'refund' ? knetDraft.parentReceiptId : null}
          allowManual={knetCfg.allow_manual_fallback}
          onApproved={handleKnetApproved}
          onManual={handleKnetManual}
          onClose={() => {
            confirmAfterRefundRef.current = false;
            setKnetDraft(null);
          }}
        />
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

interface Split {
  code: number;
  name: string;
  amount: number;
  isCash: boolean;
  /** Approved KNET terminal attempt that took this slice (KNET_TERMINAL.md). */
  knetTxnId?: number;
  /** Card summary shown under the slice (masked PAN · auth · RRN). */
  knetLabel?: string;
}

/**
 * The header CTA — either the cash-customer "Unpaid" path, or the
 * credit-customer "On Account" path. Both post the single Unpaid tender
 * (Code -1); the server turns the credit customer's into a Code 52
 * credit-sales invoice on the strength of customer_type.
 */
type HeaderCta = {
  kind: 'unpaid' | 'on-account';
  label: 'Unpaid' | 'On Account';
  type: PaymentType;
};

/**
 * Round money at the TENANT currency's precision — 3 decimals on KWD, 2 on
 * EGP/SAR/AED. Same rule (and same half-away-from-zero behaviour) as the cart,
 * the wire builder and the server; a hardcoded 2 here used to make the till
 * collect 0.14 for a 0.136 KWD order.
 */
function roundM(n: number): number {
  return roundMoney(n, moneyPrecision());
}

/**
 * Adjust the last cash split downward so Σ splits == orderTotal exactly.
 *
 * Why: the server requires payment sum to equal order net within 0.01.
 * Cashiers often enter the cash they received (e.g. 100 for a 95.50 order),
 * which would produce a sum of 100 ≠ 95.50. We trim the last cash split
 * to absorb the overage, and pass `cash_tendered` separately so the
 * receipt still records the actual cash handed over (and the change due).
 */
function computeExactSplits(splits: Split[], orderTotal: number): Split[] {
  const sum = roundM(splits.reduce((s, p) => s + p.amount, 0));
  const overage = roundM(sum - orderTotal);
  if (overage <= moneyEpsilon()) return splits;
  let lastCashIdx = -1;
  for (let i = splits.length - 1; i >= 0; i--) {
    if (splits[i]!.isCash) {
      lastCashIdx = i;
      break;
    }
  }
  if (lastCashIdx === -1) return splits;
  const adjusted = [...splits];
  adjusted[lastCashIdx] = {
    ...adjusted[lastCashIdx]!,
    amount: roundM(adjusted[lastCashIdx]!.amount - overage),
  };
  return adjusted;
}

/**
 * Combine payment rows that share a type_code into one row, summing amounts.
 *
 * Used when re-grossing an exchange's sale leg: the cashier's net tender plus
 * the returned-goods credit can land on the same method (e.g. both Cash), and
 * the receipt should show a single Cash row for the full sale total rather than
 * two.
 */
function mergePayments(
  pays: Array<{ type_code: number; amount: number; knet_terminal_txn_id?: number }>,
): Array<{ type_code: number; amount: number; knet_terminal_txn_id?: number }> {
  const byCode = new Map<number, number>();
  // A KNET terminal attempt id stays on its tender's row. Were it ever merged
  // with another slice of the same code, the server refuses the amount
  // mismatch rather than accept an unlinked card payment.
  const knetByCode = new Map<number, number>();
  for (const p of pays) {
    byCode.set(p.type_code, roundM((byCode.get(p.type_code) ?? 0) + p.amount));
    if (p.knet_terminal_txn_id != null) knetByCode.set(p.type_code, p.knet_terminal_txn_id);
  }
  return [...byCode.entries()].map(([type_code, amount]) => {
    const knet = knetByCode.get(type_code);
    return knet != null ? { type_code, amount, knet_terminal_txn_id: knet } : { type_code, amount };
  });
}

function quickAmounts(target: number): number[] {
  if (target <= 0) return [];
  const candidates = [
    Math.ceil(target),
    Math.ceil(target / 5) * 5,
    Math.ceil(target / 10) * 10,
    Math.ceil(target / 20) * 20,
    Math.ceil(target / 50) * 50,
  ];
  return [...new Set(candidates)].filter((n) => n >= target).slice(0, 4);
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
